import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import {
  IonFabButton,
  IonFabList,
  IonIcon,
  IonLoading,
  isPlatform,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import { camera, cloudUpload, push } from "ionicons/icons";
import { useContext, useState } from "react";
import {
  useDocumentManager,
  FileOptions,
  PhotoOptions,
  UploadResult,
} from "../hooks/useDocumentManager";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { useOfflineStore } from "../store/offline-mode";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";

export interface UploadOptions {
  WoNumber: string;
  cameraEnabled: boolean;
  fileEnabled: boolean;
  isSmall: boolean;
  processCompletedCallback: (isSuccess: boolean) => void;
}

const requestPermissions = async () => {
  const permissions = await Camera.requestPermissions({
    permissions: ["camera", "photos"],
  });
  return permissions;
};

const checkPermissions = async () => {
  if (!isPlatform("hybrid")) {
    return { camera: "granted", photos: "granted" };
  }
  const permissions = await Camera.checkPermissions();
  return permissions;
};

const FileUploadFAB = (options: UploadOptions) => {
  const { translations } = useContext(TranslationsContext);
  const {
    WoNumber,
    cameraEnabled,
    fileEnabled,
    isSmall,
    processCompletedCallback,
  } = options;
  const { uploadFile, uploadPhoto } = useDocumentManager();
  const [showLoading, setShowLoading] = useState(false);
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const [presentOfflineMessage] = useIonAlert();
  const isOffline = useOfflineStore((state) => state.isOffline);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { addOfflineFileToUpload, addOfflinePhotoToUpload } =
    useWoOfflineUpdatesStore();

  const showToast = (message: string) => {
    presentToast({ message, duration: 5000, position: "top" });
  };

  const showAlert = (message: string) => {
    presentAlert({ message, buttons: ["OK"] });
  };

  const uploadDoneCallback = (result: UploadResult) => {
    const message =
      typeof result.Response === "string"
        ? result.Response
        : result.Response?.Message;
    result.Success ? showToast(message) : showAlert(message);
    processCompletedCallback(result.Success);
  };

  const showNameInputAlert = async (
    defaultFileName: string,
    onSubmit: (fileName: string) => void,
    onOfflineMessage?: string
  ) => {
    presentAlert({
      message: translations["lbl_enter_file_name"] || "Please enter file name",
      backdropDismiss: false,
      inputs: [
        {
          id: "txtFileName",
          placeholder: translations["lbl_file_name"] || "File Name",
          attributes: { maxlength: 100 },
          value: defaultFileName,
        },
      ],
      buttons: [
        {
          text: translations["lbl_btn_ok"] || "OK",
          handler(value) {
            if (value?.[0]) {
              onSubmit(value[0]);
              return true;
            }
            return false;
          },
        },
        { text: translations["lbl_btn_cancel"] || "Cancel", role: "cancel" },
      ],
      onDidPresent: () =>
        (document.getElementById("txtFileName") as HTMLInputElement)?.select(),
      onDidDismiss: () => {
        if (isOffline && onOfflineMessage) {
          presentOfflineMessage({
            message: onOfflineMessage,
            buttons: [{ text: translations["lbl_btn_ok"] || "OK" }],
          });
        }
      },
    });
  };

  const selectFile = () => {
    (document.getElementById("filePicker") as HTMLInputElement)?.click();
  };

  const fileClicked = () => {
    (document.getElementById("filePicker") as HTMLInputElement).value = "";
  };

  const fileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.substring(0, file.name.lastIndexOf("."));
    const fileExtn = file.name.split(".").pop() || "";

    showNameInputAlert(
      fileName,
      (enteredName) => {
        const finalName = `${enteredName}.${fileExtn}`;
        const options: FileOptions = {
          WoNumber,
          File: file,
          FileName: finalName,
          showLoadingCallback: setShowLoading,
          uploadDoneCallback,
        };

        if (!isOffline) uploadFile(options);
        else {
          setShowLoading(false);
          addOfflineFileToUpload({ WoNumber, File: file, FileName: finalName });
        }
      },
      translatedMessages["MobOfflineFileUpload"]?.MessageText ||
        "File added in offline store."
    );
  };

  const takePhoto = async () => {
    const permissions = await checkPermissions();
    if (permissions.camera !== "granted" || permissions.photos !== "granted") {
      await requestPermissions();
    }

    const source = await presentCustomPhotoSourceDialog();
    if (source === null) return;

    if (source === CameraSource.Photos) {
      selectFile();
      return;
    }

    if (isPlatform("hybrid")) {
      await requestPermissions();
    }
    let photo: any;
    try {
      photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        allowEditing: false,
        quality: 100,
        source,
      });
    } catch (error) {
      console.error(error);
      return;
    }

    showNameInputAlert(
      "photo",
      (enteredName) => {
        const finalName = `${enteredName}.${photo.format}`;
        const options: PhotoOptions = {
          WoNumber,
          Photo: photo,
          FileName: finalName,
          showLoadingCallback: setShowLoading,
          uploadDoneCallback,
        };

        if (!isOffline) uploadPhoto(options);
        else {
          setShowLoading(false);
          addOfflinePhotoToUpload({
            WoNumber,
            Photo: photo,
            FileName: finalName,
          });
        }
      },
      translatedMessages["MobOfflinePhotoUpload"]?.MessageText ||
        "Photo added in offline store."
    );
  };

  const presentCustomPhotoSourceDialog = (): Promise<CameraSource | null> => {
    return new Promise((resolve) => {
      presentAlert({
        header: "Choose Photo Source",
        message: "Do you want to take a new photo or select an existing one?",
        buttons: [
          { text: "Take Picture", handler: () => resolve(CameraSource.Camera) },
          { text: "From Photos", handler: () => resolve(CameraSource.Photos) },
          { text: "Cancel", role: "cancel", handler: () => resolve(null) },
        ],
      });
    });
  };

  const renderFab = () => {
    if (cameraEnabled && fileEnabled) {
      if (isPlatform("ios")) {
        return (
          <IonFabButton color="primary" onClick={selectFile}>
            <IonIcon icon={cloudUpload} />
          </IonFabButton>
        );
      } else {
        return (
          <>
            <IonFabButton color="primary">
              <IonIcon icon={push} />
            </IonFabButton>
            <IonFabList side="top">
              <IonFabButton color="tertiary" onClick={takePhoto}>
                <IonIcon icon={camera} />
              </IonFabButton>
              <IonFabButton color="tertiary" onClick={selectFile}>
                <IonIcon icon={cloudUpload} />
              </IonFabButton>
            </IonFabList>
          </>
        );
      }
    }

    if (cameraEnabled) {
      return (
        <IonFabButton
          color="primary"
          size={isSmall ? "small" : undefined}
          onClick={takePhoto}
        >
          <IonIcon icon={camera} />
        </IonFabButton>
      );
    }

    if (fileEnabled) {
      return (
        <IonFabButton
          color="primary"
          size={isSmall ? "small" : undefined}
          onClick={selectFile}
        >
          <IonIcon icon={cloudUpload} />
        </IonFabButton>
      );
    }

    return null;
  };

  return (
    <>
      <input
        hidden
        type="file"
        id="filePicker"
        onChange={fileSelected}
        onClick={fileClicked}
      />
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      {renderFab()}
    </>
  );
};

export default FileUploadFAB;
